/**
 * BulkTemplateMatchWizard — bulk-review screen for Feature A (auto-map imported
 * schedule leaves against the PUBLISHED day-plan template library — no standard
 * required). On open it runs a single write-free preview (every non-summary leaf
 * scored via the per-task suggest scorer → top-3 suggestions, D4 confidence
 * bands). The planner reviews per row (accept toggle, template pick, qty/gangs
 * override), then Create mappings commits MAPPINGS ONLY with the frozen
 * bulkMatchId (server re-derives to gate; 409 BULKMATCH_STALE → auto re-preview).
 * Already-mapped tasks are read-only and skipped; hard-blocked rows are disabled.
 * Visual register: Sovereign Emerald (Antd 6 — styles.{header,body,footer}).
 * Pattern copy of standards/automatch/AutomatchWizard.
 */
import { Alert, Button, Checkbox, InputNumber, Modal, Select, Spin, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { isAxiosError } from 'axios';
import { Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import { usePreviewBulkMatch, useCommitBulkMatch } from '../hooks/useBulkTemplateMatch';
import type { BulkMatchPreview, BulkMatchRow } from '../types';
import { bandTag, defaultAccept, hardBlocks, isRowAcceptable, summarize } from './bulkMatchPresentation';

export interface BulkTemplateMatchWizardProps {
    open: boolean;
    projectId: string;
    /** default: latest ready snapshot (server resolves when omitted). */
    importId?: string;
    onClose: () => void;
    onDone: () => void;
}

function apiErrMsg(err: unknown, fallback: string): string {
    const msg = (err as { response?: { data?: { message?: string } } } | undefined)?.response
        ?.data?.message;
    return msg || fallback;
}

const CAPTION = 'mb-2 text-[10px] font-bold uppercase tracking-[0.12em]';

export function BulkTemplateMatchWizard({
    open,
    projectId,
    importId,
    onClose,
    onDone,
}: BulkTemplateMatchWizardProps) {
    const previewMut = usePreviewBulkMatch(projectId);
    const commitMut = useCommitBulkMatch(projectId);

    const [preview, setPreview] = useState<BulkMatchPreview | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    // Per-row local state, keyed by taskKey. templateMap/qtyMap/gangsMap only hold
    // USER changes; the effective value falls back to the row's prefill.
    const [acceptMap, setAcceptMap] = useState<Map<string, boolean>>(new Map());
    const [templateMap, setTemplateMap] = useState<Map<string, string>>(new Map());
    const [qtyMap, setQtyMap] = useState<Map<string, number>>(new Map());
    const [gangsMap, setGangsMap] = useState<Map<string, number>>(new Map());

    /** Seed local per-row state from a fresh preview (accept defaults to
     *  high-band, unmapped, unblocked rows with a prefilled qty). */
    const seedRowState = (p: BulkMatchPreview) => {
        const accept = new Map<string, boolean>();
        for (const r of p.rows) accept.set(r.taskKey, defaultAccept(r));
        setAcceptMap(accept);
        setTemplateMap(new Map());
        setQtyMap(new Map());
        setGangsMap(new Map());
    };

    // ── One preview per open ────────────────────────────────────────────────
    // biome-ignore lint/correctness/useExhaustiveDependencies: mutation objects are unstable per render; run once per open
    useEffect(() => {
        if (!open) {
            // reset on close
            setPreview(null);
            setPreviewError(null);
            setAcceptMap(new Map());
            setTemplateMap(new Map());
            setQtyMap(new Map());
            setGangsMap(new Map());
            return;
        }
        void runPreview();
    }, [open]);

    const runPreview = async () => {
        setPreviewError(null);
        try {
            const p = await previewMut.mutateAsync(importId);
            setPreview(p);
            seedRowState(p);
        } catch (e) {
            setPreview(null);
            setPreviewError(apiErrMsg(e, 'No ready schedule to match — import a schedule first.'));
        }
    };

    // ── Effective per-row values (user override ?? prefill) ────────────────
    const effectiveTemplateId = (row: BulkMatchRow): string | undefined =>
        templateMap.get(row.taskKey) ?? row.suggestions[0]?.templateId;
    const effectiveQty = (row: BulkMatchRow): number | null =>
        qtyMap.get(row.taskKey) ?? row.qtyPrefill?.qty ?? null;
    const effectiveGangs = (row: BulkMatchRow): number | undefined =>
        gangsMap.get(row.taskKey) ?? row.gangsPrefill ?? undefined;

    const rowDisabled = (row: BulkMatchRow): boolean =>
        row.existingMapping || row.band === 'none' || hardBlocks(row).length > 0;

    // ── Derived counts ──────────────────────────────────────────────────────
    const acceptedKeySet = useMemo(() => {
        const keys = new Set<string>();
        for (const r of preview?.rows ?? []) {
            if ((acceptMap.get(r.taskKey) ?? false) && isRowAcceptable(r, effectiveQty(r))) {
                keys.add(r.taskKey);
            }
        }
        return keys;
        // biome-ignore lint/correctness/useExhaustiveDependencies: effectiveQty is derived from qtyMap
    }, [preview, acceptMap, qtyMap]);
    const summary = preview ? summarize(preview.rows, acceptedKeySet) : null;
    const acceptedCount = acceptedKeySet.size;
    const committing = commitMut.isPending;
    const canCreate = !!preview && acceptedCount > 0 && !committing;

    // ── Commit ──────────────────────────────────────────────────────────────
    const handleCreate = async () => {
        if (!preview) return;
        const rows = preview.rows
            .filter((r) => (acceptMap.get(r.taskKey) ?? false) && isRowAcceptable(r, effectiveQty(r)))
            .map((r) => ({
                taskKey: r.taskKey,
                accept: true,
                templateId: templateMap.get(r.taskKey) ?? (r.suggestions[0]?.templateId as string),
                totalQty: effectiveQty(r) as number,
                ...(effectiveGangs(r) !== undefined ? { gangs: effectiveGangs(r) as number } : {}),
            }));
        if (rows.length === 0) return;
        try {
            const res = await commitMut.mutateAsync({
                bulkMatchId: preview.bulkMatchId,
                ...(importId ? { importId } : {}),
                rows,
            });
            message.success(
                `Mapped ${res.created} task(s)` +
                    `${res.skipped ? `, ${res.skipped} skipped` : ''}` +
                    `${res.failed.length ? `, ${res.failed.length} failed` : ''}`,
            );
            onDone();
            onClose();
        } catch (e) {
            if (isAxiosError(e) && e.response?.status === 409) {
                // BULKMATCH_STALE — templates or the import changed. Auto re-preview.
                message.warning('Templates or the import changed — preview refreshed');
                await runPreview();
                return;
            }
            message.error(apiErrMsg(e, 'Create mappings failed'));
        }
    };

    // ── Table columns ───────────────────────────────────────────────────────
    const columns: ColumnsType<BulkMatchRow> = [
        {
            title: 'Accept',
            key: 'accept',
            width: 72,
            render: (_v, row) => (
                <Checkbox
                    checked={(acceptMap.get(row.taskKey) ?? false) && isRowAcceptable(row, effectiveQty(row))}
                    disabled={rowDisabled(row)}
                    aria-label={`Accept ${row.taskName}`}
                    onChange={(e) =>
                        setAcceptMap((prev) => {
                            const next = new Map(prev);
                            next.set(row.taskKey, e.target.checked);
                            return next;
                        })
                    }
                />
            ),
        },
        {
            title: 'Task',
            key: 'task',
            render: (_v, row) => {
                const hard = hardBlocks(row);
                return (
                    <div>
                        <div
                            className="text-[12.5px] font-semibold"
                            style={{ color: row.existingMapping ? SOVEREIGN.kpiCaption : SOVEREIGN.ink }}
                        >
                            {row.taskName}
                        </div>
                        <div className="font-mono text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                            {row.taskKey}
                            {row.orgUnitName && <span className="ml-2 font-sans">{row.orgUnitName}</span>}
                            {row.existingMapping && (
                                <span className="ml-2 font-sans italic">already mapped</span>
                            )}
                        </div>
                        {hard.length > 0 && (
                            <div className="text-[11px]" style={{ color: '#b3261e' }}>
                                {hard.join(' · ')}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Suggested template',
            key: 'template',
            width: 300,
            render: (_v, row) =>
                row.suggestions.length > 0 ? (
                    <Select
                        size="small"
                        value={effectiveTemplateId(row)}
                        disabled={rowDisabled(row)}
                        aria-label={`Template for ${row.taskName}`}
                        options={row.suggestions.map((s) => ({
                            value: s.templateId,
                            label: `${s.name} v${s.templateVersion} · ${Math.round(s.score)}%`,
                        }))}
                        onChange={(v) =>
                            setTemplateMap((prev) => {
                                const next = new Map(prev);
                                next.set(row.taskKey, v);
                                return next;
                            })
                        }
                        style={{ width: '100%' }}
                    />
                ) : (
                    <span className="text-[11.5px] italic" style={{ color: SOVEREIGN.kpiCaption }}>
                        no suggestion
                    </span>
                ),
        },
        {
            title: 'Confidence',
            key: 'band',
            width: 96,
            render: (_v, row) => <Tag color={bandTag(row.band).color}>{bandTag(row.band).label}</Tag>,
        },
        {
            title: 'Qty',
            key: 'qty',
            width: 160,
            render: (_v, row) => {
                const selected = row.suggestions.find((s) => s.templateId === effectiveTemplateId(row));
                return (
                    <div className="flex items-center gap-2">
                        <InputNumber
                            size="small"
                            min={0}
                            value={effectiveQty(row)}
                            disabled={rowDisabled(row)}
                            aria-label={`Quantity for ${row.taskName}`}
                            onChange={(v) =>
                                setQtyMap((prev) => {
                                    const next = new Map(prev);
                                    if (typeof v === 'number' && v > 0) next.set(row.taskKey, v);
                                    else next.delete(row.taskKey);
                                    return next;
                                })
                            }
                            style={{
                                width: 90,
                                background: SOVEREIGN.inputBg,
                                borderColor: SOVEREIGN.inputBorder,
                            }}
                        />
                        <span className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                            {selected?.unit}
                        </span>
                    </div>
                );
            },
        },
        {
            title: 'Gangs',
            key: 'gangs',
            width: 96,
            render: (_v, row) => (
                <InputNumber
                    size="small"
                    min={1}
                    precision={0}
                    value={effectiveGangs(row) ?? null}
                    disabled={rowDisabled(row)}
                    aria-label={`Gangs for ${row.taskName}`}
                    onChange={(v) =>
                        setGangsMap((prev) => {
                            const next = new Map(prev);
                            if (typeof v === 'number' && v >= 1) next.set(row.taskKey, Math.round(v));
                            else next.delete(row.taskKey);
                            return next;
                        })
                    }
                    style={{ width: 72, background: SOVEREIGN.inputBg, borderColor: SOVEREIGN.inputBorder }}
                />
            ),
        },
    ];

    const previewing = previewMut.isPending;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={1100}
            destroyOnHidden
            maskClosable={false}
            title={
                <span className="inline-flex items-center gap-2">
                    <Sparkles size={16} aria-hidden style={{ color: SOVEREIGN.brand }} />
                    <span className="text-[15px] font-bold" style={{ color: SOVEREIGN.title }}>
                        Auto-map templates (library)
                    </span>
                    {preview && (
                        <span
                            className="text-[11.5px] font-semibold"
                            style={{ color: SOVEREIGN.kpiCaption }}
                        >
                            published template library
                        </span>
                    )}
                </span>
            }
            styles={{
                header: {
                    background: SOVEREIGN.canvas,
                    borderBottom: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingBottom: 12,
                },
                body: {
                    background: SOVEREIGN.paper,
                    padding: 20,
                    maxHeight: '72vh',
                    overflowY: 'auto',
                },
                footer: {
                    background: SOVEREIGN.canvas,
                    borderTop: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingTop: 12,
                },
            }}
            footer={
                <div className="flex items-center justify-between gap-3">
                    <span
                        className="text-[11.5px] font-semibold tabular-nums"
                        style={{ color: SOVEREIGN.kpiCaption }}
                    >
                        {summary
                            ? `${summary.high} high · ${summary.medium} medium · ${summary.none} unmatched · ${summary.alreadyMapped} already mapped`
                            : ''}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={committing}
                            disabled={!canCreate}
                            onClick={() => void handleCreate()}
                            style={
                                canCreate
                                    ? { background: SOVEREIGN.btn, borderColor: SOVEREIGN.btnBorder }
                                    : undefined
                            }
                        >
                            Create mappings ({acceptedCount})
                        </Button>
                    </div>
                </div>
            }
        >
            {previewing && !preview ? (
                <div className="flex justify-center py-16">
                    <Spin />
                </div>
            ) : previewError ? (
                <Alert
                    type="error"
                    showIcon
                    message="Could not run bulk template match"
                    description={previewError}
                />
            ) : preview ? (
                <section>
                    <div className={CAPTION} style={{ color: SOVEREIGN.kpiCaption }}>
                        Imported leaf tasks vs published templates
                    </div>
                    <Table<BulkMatchRow>
                        size="small"
                        rowKey="taskKey"
                        columns={columns}
                        dataSource={preview.rows}
                        pagination={false}
                        locale={{ emptyText: 'No non-summary leaf tasks in the schedule.' }}
                    />
                </section>
            ) : null}
        </Modal>
    );
}

export default BulkTemplateMatchWizard;
